import { Stack } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Colors, Icons } from 'copyright-clearance-center-common-ui';
import { useEffect, useState } from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		header: {
			display: 'flex',
			padding: '0.25rem 0.25rem 0.25rem 0rem',
			alignItems: 'center',
			justifyContent: 'space-between',
			height: '2rem',
			borderBottom: `1px solid ${Colors.neutral300}`
		},
		title: {
			display: 'flex',
			alignItems: 'center',
			color: '#485B6E',
			userSelect: 'none',
			fontSize: '0.75rem'
		}
	})
);

export interface IDocumentDetailsCollapseProps {
	title: string;
	collapsed: boolean;
	editable?: boolean;
	children?: React.ReactNode;
	onEdit?: () => void;
	onSave?: () => void;
	onCancel?: () => void;
	onOpen?: () => void;
	editMode?: boolean;
	disabled?: boolean;
	disableCollapsing?: boolean;
	testId?: string;
	buttonsContainerClassName?: string;
	useBigButtons?: boolean;
	buttonsSpacing?: number;
	updatesCount?: number;
}

const DocumentDetailsCollapse: React.FunctionComponent<IDocumentDetailsCollapseProps> = props => {
	const [collapsed, setCollapsed] = useState(props.collapsed);
	const classes = useStyles();
	useEffect(() => setCollapsed(props.editMode ? false : collapsed), [props.editMode]);
	const saveButtonTextLabel = localize('Save');

	const getSaveButtonLabel = () => {
		if (props.updatesCount > 0) {
			return `${saveButtonTextLabel} (${props.updatesCount})`;
		}

		return saveButtonTextLabel;
	};

	return (
		<Stack spacing={2} data-test-id={`${props.testId || 'item'}-section`} height="100%">
			<Stack spacing={1} overflow="unset">
				<div
					className={classes.header}
					data-test-id={`${props.testId || 'item'}-header`}
					onClick={() => {
						if (!props.editMode && !props.disableCollapsing) {
							if (props.onOpen) {
								props.onOpen();
							}

							setCollapsed(!collapsed);
						}
					}}
				>
					<div className={classes.title}>
						{!props.disableCollapsing && (
							<span
								className="rup-icon rup-icon--sm"
								dangerouslySetInnerHTML={{
									__html: collapsed ? Icons.ChevronRightIcon : Icons.ChevronDownIcon
								}}
							/>
						)}
						{props.title}
					</div>
					{props.editable && !props.editMode && (
						<button
							type="button"
							className="rup-btn rup-btn--sm rup-btn--text"
							data-test-id={`${props.testId || 'item'}-edit-btn`}
							onClick={e => {
								if (props.onEdit) {
									props.onEdit();
								}

								e.stopPropagation();
							}}
						>
							<span dangerouslySetInnerHTML={{ __html: Icons.EditIcon }} />
							{localize('Edit')}
						</button>
					)}
				</div>
				{!collapsed && props.children}
			</Stack>
			{props.editMode && (
				<Stack
					spacing={1}
					className={clsx({ [props.buttonsContainerClassName]: !!props.buttonsContainerClassName })}
					direction="row-reverse"
				>
					<button
						disabled={props.disabled}
						type="button"
						className={clsx('rup-btn', 'rup-btn--contained', {
							['rup-btn--sm']: !props.useBigButtons
						})}
						onClick={e => {
							if (!props.disableCollapsing) {
								e.stopPropagation();
							}

							if (props.onSave) {
								props.onSave();
							}
						}}
					>
						{getSaveButtonLabel()}
					</button>
					<button
						disabled={props.disabled}
						type="button"
						className={clsx('rup-btn', { ['rup-btn--sm']: !props.useBigButtons })}
						onClick={e => {
							if (props.onCancel) {
								props.onCancel();
							}

							e.stopPropagation();
						}}
					>
						{localize('Cancel')}
					</button>
				</Stack>
			)}
		</Stack>
	);
};

export default DocumentDetailsCollapse;
